import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import ProjectCard from './components/ProjectCard';
import ProjectStoryViewer from './components/ProjectStoryViewer';
import { projects, projectCategories, Project } from '@/mocks/projects';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const stats = [
  { value: '+25', label: 'Años de experiencia' },
  { value: String(projects.length), label: 'Proyectos ejecutados' },
  { value: '20+', label: 'Ciudades de cobertura' },
  { value: '4.5★', label: 'Google Reviews' },
];

const reveal = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal ${r.isVisible ? 'reveal-visible' : ''}`;
const revealLeft = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-left ${r.isVisible ? 'reveal-visible' : ''}`;
const revealRight = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-right ${r.isVisible ? 'reveal-visible' : ''}`;
const revealScale = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-scale ${r.isVisible ? 'reveal-visible' : ''}`;

const isVideo = (url: string) => /\.(mp4|mov|webm|ogg)(\?.*)?$/i.test(url);

const ProyectosPage = () => {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const heroR = useScrollAnimation();
  const statsR = useScrollAnimation();
  const galleryR = useScrollAnimation();
  const featuredR = useScrollAnimation();
  const videosR = useScrollAnimation();
  const ctaR = useScrollAnimation();

  const handleNextProject = () => {
    if (!selectedProject) return;
    const idx = projects.findIndex((p) => p.id === selectedProject.id);
    const next = projects[idx + 1];
    if (next) setSelectedProject(next);
  };

  const handlePrevProject = () => {
    if (!selectedProject) return;
    const idx = projects.findIndex((p) => p.id === selectedProject.id);
    const prev = projects[idx - 1];
    if (prev) setSelectedProject(prev);
  };

  const filtered = projects.filter((p) => {
    const matchCat = activeCategory === 'Todos' || p.category === activeCategory;
    const matchSearch =
      searchQuery === '' ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  // Collect all videos with their project info
  const allVideos = projects
    .filter((p) => p.videos && p.videos.length > 0)
    .flatMap((p) =>
      (p.videos || []).map((url, i) => ({
        url,
        projectTitle: p.title,
        projectCategory: p.category,
        projectLocation: p.location,
        poster: p.imgs[0],
        id: `${p.id}-${i}`,
      }))
    );

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-24 overflow-hidden bg-[#0C1D2E]">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20industrial%20steel%20warehouse%20construction%20site%20Chile%2C%20metal%20framework%20structure%20under%20clear%20blue%20sky%20with%20warm%20sunlight%2C%20professional%20architectural%20photography%2C%20wide%20angle%20view%20showing%20impressive%20scale%20and%20industrial%20design%2C%20bright%20corporate%20photography%20style%2C%20clean%20aesthetic&width=1800&height=600&seq=projHero002&orientation=landscape"
              alt="Galpón industrial en construcción - Proyectos IngemetalSur"
              className="w-full h-full object-cover object-center opacity-75"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C1D2E]/70 via-[#0C1D2E]/50 to-[#0C1D2E]/20"></div>
          </div>
          <div ref={heroR.ref} className={`relative z-10 max-w-7xl mx-auto px-4 md:px-8 ${reveal(heroR)}`}>
            <div className="max-w-2xl">
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Portafolio</span>
              <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mt-3 mb-5 leading-[1.1]">
                Nuestros<br />
                <span className="text-[#C88A2A]">Proyectos</span>
              </h1>
              <p className="text-slate-200 text-lg leading-relaxed">
                Más de 25 años construyendo galpones industriales, estructuras metálicas, obras civiles y proyectos mineros en todo Chile.
              </p>
            </div>
          </div>
        </section>

        {/* Stats bar */}
        <section className="bg-[#0C1D2E] border-b border-white/5">
          <div ref={statsR.ref} className={`max-w-7xl mx-auto px-4 md:px-8 ${reveal(statsR)}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
              {stats.map((s, i) => (
                <div key={s.label} className={`py-10 md:py-12 px-4 md:px-6 text-center reveal reveal-delay-${(i + 1) * 100} ${statsR.isVisible ? 'reveal-visible' : ''}`}>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-white mb-1">{s.value}</div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Gallery */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {/* Header */}
            <div ref={galleryR.ref} className={`flex flex-col md:flex-row gap-6 md:items-end justify-between mb-12 ${reveal(galleryR)}`}>
              <div>
                <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Portafolio Industrial</span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mt-3">
                  Obras ejecutadas
                </h2>
              </div>
              <p className="text-[#475569] text-sm max-w-md">
                Galpones, estructuras metálicas, locales comerciales, proyectos mineros e infraestructura acuícola.
              </p>
            </div>

            {/* Filters + Search */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-10">
              <div className="flex flex-wrap gap-2">
                {projectCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 ${
                      activeCategory === cat
                        ? 'bg-[#0C1D2E] text-white'
                        : 'bg-[#F8FAFC] text-[#475569] hover:bg-[#F0F0F0] border border-[#E2E8F0]'
                    }`}
                  >
                    {cat}
                    {cat !== 'Todos' && (
                      <span className={`ml-1.5 text-xs ${activeCategory === cat ? 'text-white/60' : 'text-[#64748B]'}`}>
                        ({projects.filter((p) => p.category === cat).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="relative w-full md:w-72">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm"></i>
                <input
                  type="text"
                  placeholder="Buscar proyecto o ciudad..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-[#E2E8F0] rounded-full text-sm text-[#0C1D2E] bg-[#F8FAFC] focus:outline-none focus:border-[#C88A2A] transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0C1D2E] cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Results count */}
            <div className="mb-8 flex items-center justify-between">
              <p className="text-[#64748B] text-sm">
                Mostrando <strong className="text-[#0C1D2E]">{filtered.length}</strong> de {projects.length} proyectos
              </p>
            </div>

            {/* Grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((project, i) => (
                  <div key={project.id} className={`reveal ${galleryR.isVisible ? 'reveal-visible' : ''}`} style={{ transitionDelay: `${i * 80}ms` }}>
                    <ProjectCard
                      project={project}
                      onClick={setSelectedProject}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-[#F8FAFC] rounded-full mx-auto mb-4">
                  <i className="ri-search-line text-[#64748B] text-2xl"></i>
                </div>
                <p className="text-[#475569] text-base font-medium">No se encontraron proyectos</p>
                <p className="text-[#64748B] text-sm mt-1">Intenta con otra categoría o búsqueda</p>
                <button
                  onClick={() => { setActiveCategory('Todos'); setSearchQuery(''); }}
                  className="mt-4 text-[#C88A2A] text-sm font-semibold hover:underline cursor-pointer"
                >
                  Ver todos los proyectos
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Featured project highlight */}
        <section className="py-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={featuredR.ref} className={`text-center mb-14 ${reveal(featuredR)}`}>
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Proyecto destacado</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mt-3">
                Concesionaria Volvo Temuco
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className={`relative ${revealLeft(featuredR)}`}>
                <div className="w-full h-80 rounded-lg overflow-hidden bg-[#0C1D2E]">
                  <img
                    src="https://i.imgur.com/tl7d4Yb.jpg"
                    alt="Concesionaria Volvo Temuco construida por IngemetalSur"
                    className="w-full h-full object-contain object-center bg-[#0C1D2E] hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-[#0C1D2E] text-white p-5 rounded-lg hidden md:block hover:scale-105 transition-transform duration-200">
                  <div className="text-2xl font-heading font-bold">1.800</div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">m² construidos</div>
                </div>
              </div>
              <div className={revealRight(featuredR)}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="bg-[#0C1D2E] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Locales Comerciales</span>
                  <span className="text-[#64748B] text-sm">· Temuco · 2019</span>
                </div>
                <p className="text-[#475569] text-base leading-relaxed mb-8">
                  Construcción completa de la concesionaria oficial Volvo en la salida sur de Temuco. Proyecto de alta exigencia con fachada corporativa, amplios espacios interiores para exhibición de vehículos, zonas de servicio técnico y terminaciones premium. Un proyecto emblemático que consolidó nuestra experiencia en locales comerciales de primer nivel.
                </p>
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[
                    { icon: 'ri-ruler-line', value: '1.800 m²', label: 'Superficie' },
                    { icon: 'ri-calendar-line', value: '2019', label: 'Año' },
                    { icon: 'ri-map-pin-line', value: 'Temuco', label: 'Ubicación' },
                  ].map((item) => (
                    <div key={item.label} className="bg-white border border-[#E2E8F0] rounded-lg p-4 text-center hover:border-[#C88A2A]/20 hover:shadow-md transition-all duration-200">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#0C1D2E] rounded-md mx-auto mb-2 hover:bg-[#C88A2A] transition-colors duration-200">
                        <i className={`${item.icon} text-white text-sm`}></i>
                      </div>
                      <div className="font-heading font-bold text-[#0C1D2E] text-sm">{item.value}</div>
                      <div className="text-[#64748B] text-xs">{item.label}</div>
                    </div>
                  ))}
                </div>
                <a
                  href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20un%20proyecto%20similar%20a%20la%20concesionaria%20Volvo"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#C88A2A] hover:bg-[#A06B1A] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Quiero un proyecto similar
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Videos de Proyectos */}
        {allVideos.length > 0 && (
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
              <div ref={videosR.ref} className={`flex items-end justify-between mb-12 ${reveal(videosR)}`}>
                <div>
                  <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Documentación técnica</span>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mt-3">
                    Videos de Proyectos
                  </h2>
                </div>
                <div className="hidden md:flex items-center gap-2 text-[#64748B] text-sm">
                  <i className="ri-video-line text-[#C88A2A]"></i>
                  <span>{allVideos.length} videos disponibles</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {allVideos.map((video, i) => (
                  <div key={video.id} className={`rounded-xl overflow-hidden bg-[#0C1D2E] group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 reveal ${videosR.isVisible ? 'reveal-visible' : ''}`} style={{ transitionDelay: `${i * 100}ms` }}>
                    <div className="relative aspect-video">
                      <video
                        src={video.url}
                        controls
                        preload="metadata"
                        poster={video.poster}
                        className="w-full h-full object-cover"
                        aria-label={`Video de ${video.projectTitle}`}
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#C88A2A]/20 text-[#C88A2A] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                          {video.projectCategory}
                        </span>
                        <span className="text-white/40 text-xs flex items-center gap-1">
                          <i className="ri-map-pin-line"></i>
                          {video.projectLocation}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold text-sm leading-snug">
                        {video.projectTitle}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-20 bg-[#0C1D2E]">
          <div ref={ctaR.ref} className={`max-w-3xl mx-auto px-4 md:px-8 text-center ${revealScale(ctaR)}`}>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              ¿Tu proyecto podría ser el próximo?
            </h2>
            <p className="text-white/40 text-base mb-8">
              Contáctanos hoy y recibe una cotización personalizada para tu obra en cualquier parte de Chile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizaci%C3%B3n"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#C88A2A] hover:bg-[#A06B1A] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </a>
              <a
                href="tel:+56992265118"
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 hover:scale-[1.02] active:scale-[0.98] border border-white/10 text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                +56 9 9226 5118
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />

      {/* Modal */}
      <ProjectStoryViewer
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onNextProject={handleNextProject}
        onPrevProject={handlePrevProject}
      />
    </>
  );
};

export default ProyectosPage;